// CHAT RENDER TELEMETRY — a postmortem trail for a phone that stuttered.
//
// Three things get recorded: how long a chat render took, every receipt state
// transition, and any exception thrown while a message was rendering. It is a
// bounded ring in memory with a small persisted tail, so it survives a reload
// without ever growing unbounded. Nothing leaves the device.

import { redactText } from "@/lib/sophia/redact";

export type ChatEventKind = "render" | "receipt" | "exception" | "note";

export interface ChatEvent {
  id: string;
  at: number;
  kind: ChatEventKind;
  label: string;
  /** milliseconds, for render events */
  ms?: number;
  detail?: string;
}

const RING = 240;
const KEY = "sophia:chat:telemetry";
const events: ChatEvent[] = [];
const listeners = new Set<() => void>();

let loaded = false;
function load() {
  if (loaded || typeof localStorage === "undefined") return;
  loaded = true;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ChatEvent[];
      if (Array.isArray(parsed)) events.push(...parsed.slice(-RING));
    }
  } catch { /* a corrupt tail is not worth a crash */ }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function saveSoon() {
  if (typeof localStorage === "undefined") return;
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try { localStorage.setItem(KEY, JSON.stringify(events.slice(-80))); } catch { /* full quota */ }
  }, 1500);
}

function emit() { for (const l of listeners) { try { l(); } catch { /* ignore */ } } }

export function subscribeChatTelemetry(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function push(e: Omit<ChatEvent, "id" | "at">) {
  load();
  events.push({
    id: Math.random().toString(36).slice(2, 10),
    at: Date.now(),
    ...e,
    label: redactText(e.label).slice(0, 160),
    ...(e.detail ? { detail: redactText(e.detail).slice(0, 400) } : {}),
  });
  if (events.length > RING) events.splice(0, events.length - RING);
  saveSoon();
  emit();
}

/** How long a chat render pass took, and how many rows it painted. */
export function noteChatRender(ms: number, rendered: number, total: number): void {
  // Only worth a line when it actually cost something — otherwise every frame
  // would flood the ring and become its own performance problem.
  if (ms < 24) return;
  push({
    kind: "render",
    label: `chat render ${Math.round(ms)}ms`,
    ms: Math.round(ms),
    detail: `${rendered} of ${total} messages painted`,
  });
}

/** Every receipt state change, so a lost message can be traced afterwards. */
export function noteReceiptChange(id: string, from: string, to: string, note = ""): void {
  push({ kind: "receipt", label: `${from || "new"} → ${to}`, detail: `${id.slice(0, 12)}${note ? ` · ${note}` : ""}` });
}

/** A message that threw while rendering. The boundary keeps the rest readable. */
export function noteChatException(where: string, err: unknown, detail = ""): void {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  push({ kind: "exception", label: `${where} — ${msg}`, detail: detail || (err instanceof Error ? (err.stack ?? "") : "") });
}

export function noteChatNote(label: string, detail = ""): void {
  push({ kind: "note", label, detail });
}

export function chatEvents(): ChatEvent[] {
  load();
  return events.slice().reverse();
}

export function chatTelemetrySummary(): { renders: number; slow: number; worstMs: number; exceptions: number; receipts: number } {
  load();
  const renders = events.filter((e) => e.kind === "render");
  return {
    renders: renders.length,
    slow: renders.filter((e) => (e.ms ?? 0) > 120).length,
    worstMs: renders.reduce((m, e) => Math.max(m, e.ms ?? 0), 0),
    exceptions: events.filter((e) => e.kind === "exception").length,
    receipts: events.filter((e) => e.kind === "receipt").length,
  };
}

export function clearChatTelemetry(): void {
  events.length = 0;
  try { localStorage?.removeItem(KEY); } catch { /* ignore */ }
  emit();
}

/** A redacted report, safe to hand to anyone helping debug. */
export function chatTelemetryReport(): string {
  const s = chatTelemetrySummary();
  const lines = [
    "SOPHIA — CHAT RENDER TELEMETRY",
    `Generated ${new Date().toISOString()}`,
    `Renders logged ${s.renders} · slow (>120ms) ${s.slow} · worst ${s.worstMs}ms · exceptions ${s.exceptions} · receipt changes ${s.receipts}`,
    "",
  ];
  for (const e of chatEvents()) {
    lines.push(`${new Date(e.at).toISOString()}  [${e.kind}] ${e.label}${e.detail ? `\n    ${e.detail}` : ""}`);
  }
  return lines.join("\n");
}

/** The bounded ring as a redacted JSON bug report (already-redacted fields). */
export function chatTelemetryJson(): string {
  return JSON.stringify({
    kind: "sophia.chat-telemetry",
    generated: new Date().toISOString(),
    agent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
    viewport: typeof window !== "undefined" ? { w: window.innerWidth, h: window.innerHeight } : null,
    summary: chatTelemetrySummary(),
    events: chatEvents(),
  }, null, 2);
}

/** Save the report to the reader's device. */
export function downloadChatTelemetry(): void {
  if (typeof window === "undefined") return;
  const blob = new Blob([chatTelemetryJson()], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sophia-chat-telemetry-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
